import React, { useMemo } from 'react';
import { useTable, useSortBy, Column } from 'react-table';
import { AdminReservationDto } from 'services/data-contracts';
import TableBody from 'components/table/TableBody';
import PaginationWrapper from '../Pagination';
import styled from 'styled-components';
import { useStoreWrapper } from 'stores/StoreHelper';
import { observer } from 'mobx-react-lite';
/**
 * @description 예약 테이블 컴포넌트
 * @param reservations 예약 데이터
 * @param onRowClick 행 클릭 이벤트
 */
interface ReservationsTableProps {
  reservations: AdminReservationDto[];
  onRowClick?: (reservation: AdminReservationDto) => void;
}

const ReservationsTable = observer(({ reservations, onRowClick }: ReservationsTableProps) => {
  const { reservationFilterStore } = useStoreWrapper();
  // 테이블 컬럼 정의
  const columns = useMemo(
    () => [
      {
        Header: '예약일', // 헤더 텍스트
        accessor: 'reservationDate' as keyof AdminReservationDto, // 데이터 접근 키
        width: 120, // 너비
        Cell: ({ value }: { value: string }) => {
          // 셀 렌더링 함수
          if (!value) return '-'; // 값이 없으면 '-' 반환
          const date = new Date(value);
          return date
            .toLocaleDateString('ko-KR', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            })
            .replace(/\. /g, '-')
            .replace(/\.$/, '');
        },
      },
      {
        Header: '예약번호',
        accessor: 'reservationNo' as keyof AdminReservationDto,
        width: 120,
      },
      {
        Header: 'PMS예약번호',
        accessor: 'hostawayReservationId' as keyof AdminReservationDto,
        width: 120,
      },
      {
        Header: '상태',
        accessor: 'status' as keyof AdminReservationDto,
        width: 80,
        Cell: ({ value }: { value: string }) => {
          let backgroundColor = '#f0f0f0';
          let color = '#000000';

          switch (value) {
            case 'CONFIRMED':
              backgroundColor = '#e6f7ff';
              color = '#1890ff';
              break;
            case 'CANCELLED':
              backgroundColor = '#fff1f0';
              color = '#ff4d4f';
              break;
            case 'PENDING':
              backgroundColor = '#fffbe6';
              color = '#faad14';
              break;
            case 'COMPLETED':
              backgroundColor = '#f6ffed';
              color = '#52c41a';
              break;
            default:
              break;
          }

          return (
            <div
              style={{
                backgroundColor,
                color,
                padding: '4px 8px',
                borderRadius: '4px',
                display: 'inline-block',
                textAlign: 'center',
                fontWeight: 500,
              }}
            >
              {value}
            </div>
          );
        },
      },
      {
        Header: '숙소명',
        accessor: 'listingName' as keyof AdminReservationDto,
        width: 120,
      },
      {
        Header: '이용자',
        accessor: 'guestFirstName' as keyof AdminReservationDto,
        width: 100,
        Cell: ({ row }: { row: any }) => {
          return `${row.original.guestFirstName || ''} ${row.original.guestLastName || ''}`;
        },
      },
      {
        Header: '연락처',
        accessor: 'guestPhoneNumber' as keyof AdminReservationDto,
        width: 120,
      },
      {
        Header: '체크인',
        accessor: 'arrivalDate' as keyof AdminReservationDto,
        width: 120,
        Cell: ({ value }: { value: string }) => {
          if (!value) return '-';
          const date = new Date(value);
          return date
            .toLocaleDateString('ko-KR', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            })
            .replace(/\. /g, '-')
            .replace(/\.$/, '');
        },
      },
      {
        Header: '체크아웃',
        accessor: 'departureDate' as keyof AdminReservationDto,
        width: 120,
        Cell: ({ value }: { value: string }) => {
          if (!value) return '-';
          const date = new Date(value);
          return date
            .toLocaleDateString('ko-KR', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            })
            .replace(/\. /g, '-')
            .replace(/\.$/, '');
        },
      },
      {
        Header: '인원',
        accessor: 'totalGuests' as keyof AdminReservationDto,
        width: 80,
      },
      {
        Header: '바우처',
        accessor: 'voucherName' as keyof AdminReservationDto,
        width: 80,
      },
      {
        Header: '바우처 할인',
        accessor: 'voucherDiscountAmount' as keyof AdminReservationDto,
        width: 80,
        Cell: ({ value }: { value: number }) => {
          if (value === undefined || value === null) return '-';
          return value.toLocaleString('ko-KR') + '원';
        },
      },
      {
        Header: '쿠폰',
        accessor: 'couponName' as keyof AdminReservationDto,
        width: 80,
      },
      {
        Header: '쿠폰 할인',
        accessor: 'couponDiscountAmount' as keyof AdminReservationDto,
        width: 80,
        Cell: ({ value }: { value: number }) => {
          if (value === undefined || value === null) return '-';
          return value.toLocaleString('ko-KR') + '원';
        },
      },
      {
        Header: '실 결제액',
        accessor: 'totalAmount' as keyof AdminReservationDto,
        width: 80,
        Cell: ({ value }: { value: number }) => {
          if (value === undefined || value === null) return '-';
          return value.toLocaleString('ko-KR') + '원';
        },
      },

      {
        Header: '취소일',
        accessor: 'cancelDate' as keyof AdminReservationDto,
        width: 120,
        Cell: ({ value }: { value: string }) => {
          if (!value) return '-';
          const date = new Date(value);
          return date
            .toLocaleDateString('ko-KR', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            })
            .replace(/\. /g, '-')
            .replace(/\.$/, '');
        },
      },
    ],
    [],
  ) as Column<AdminReservationDto>[];

  return (
    <div>
      <TableContainer>
        <TableBody columns={columns} data={reservations} onRowClick={onRowClick} />
      </TableContainer>
      <PaginationContainer>
        <PaginationWrapper
          count={reservationFilterStore.paginationInfo?.totalCount ?? 0}
          totalPages={reservationFilterStore.paginationInfo?.totalPages ?? 0}
        />
      </PaginationContainer>
    </div>
  );
});

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  margin-bottom:100px;

  /* 테이블 내부 요소가 충분한 너비를 가지도록 설정 */
  & > div {
    min-width: 1200px; /* 필요에 따라 조정하세요 */
  }

  /* Sticky 헤더 설정 유지 */
  & thead,
  & .header-group {
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: #fff; /* 배경색 설정 */
  }

  /* Sticky 헤더 테두리 유지를 위한 설정 */
  & thead::after,
  & .header-group::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    border-bottom: 1px solid #ddd; /* 테두리 스타일 */
  }
`;

const PaginationContainer = styled.div`
  margin-top: 20px;
  position: fixed;
  bottom: 0;
  z-index: 10;
  margin: auto;
  background: rgba(255, 255, 255, 1);
  backdrop-filter: blur(10px);
  left: 0;
  right: 0;

  display: flex;
  justify-content: center;
  align-items: center;
`;

export default ReservationsTable;
